'use client';

import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

/**
 * The look of a list filter chip, for the `FilterChip` and for the chips that open a menu
 * (`data-active` while the menu holds a value), so a row that mixes the two reads as one.
 */
const filterChipVariants = cva(
  "inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-full border border-input bg-card px-3 text-detail-strong whitespace-nowrap outline-none transition-colors hover:bg-sand-2 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-pressed:bg-sand-3 data-[active=true]:bg-sand-3 motion-reduce:transition-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      tone: {
        default:
          'text-foreground aria-pressed:border-foreground data-[active=true]:border-foreground',
        /** A negative status (refused, cancelled): red text, red border when on. */
        danger:
          'text-destructive aria-pressed:border-destructive data-[active=true]:border-destructive',
      },
    },
    defaultVariants: { tone: 'default' },
  },
);

/**
 * The filter chip of a list (Figma «Meus pedidos v3»): a `Toggle` with `aria-pressed`, 32 tall.
 * Pressed is the sand-3 fill with a foreground border, not the ink fill of a primary `Button`: a
 * row of chips is a filter, never the page's main action. A row wider than its column scrolls
 * with `scroll-fade-x` and no scrollbar.
 */
function FilterChip({
  className,
  tone,
  ...props
}: TogglePrimitive.Props & VariantProps<typeof filterChipVariants>) {
  return (
    <TogglePrimitive
      data-slot="filter-chip"
      className={cn(filterChipVariants({ tone }), className)}
      {...props}
    />
  );
}

export { FilterChip, filterChipVariants };
